import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  forwardRef,
  OnInit,
  inject,
  DestroyRef,
  ElementRef,
  ViewChild,
  effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

export interface FilterOption<T = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

type SelectorSize = 'default' | 'small' | 'large';

@Component({
  selector: 'app-multi-select',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
  ],
  styleUrls: ['./multi-select.component.scss'],
  template: `
    <div class="selector-container general-font" [class.full-width]="fullWidth">
      <div
        #selectorElement
        class="selector-trigger"
        [class.size-default]="size === 'default'"
        [class.size-small]="size === 'small'"
        [class.size-large]="size === 'large'"
        [class.focused]="isFocused()"
        [class.disabled]="disabled"
        [class.full-width]="fullWidth"
        (click)="toggleDropdown()"
        (blur)="onBlur()"
        tabindex="0"
        role="combobox"
        [attr.aria-expanded]="isOpen()"
        [attr.aria-haspopup]="true"
        [attr.aria-disabled]="disabled"
      >
        <span
          class="selector-text"
          [class.placeholder]="!hasSelection()"
          [class.has-value]="hasSelection()"
          [class.todos-display]="isShowingTodos()"
        >
          {{ triggerText() }}
        </span>
        <svg
          class="selector-icon"
          [class.open]="isOpen()"
          [class.todos-icon]="isShowingTodos()"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M7 10L12 15L17 10"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <label
          class="selector-label"
          [class.active]="hasDisplayValue() || isOpen()"
          [class.inactive]="!hasDisplayValue() && !isOpen()"
        >
          {{ label }}
        </label>
      </div>
      @if (isOpen()) {
      <div
        class="dropdown-menu"
        [class.position-top]="dropdownPosition() === 'top'"
        [class.position-bottom]="dropdownPosition() === 'bottom'"
        (click)="$event.stopPropagation()"
      >
        @if (searchable) {
        <div class="search-container">
          <input
            type="text"
            class="search-input"
            [placeholder]="searchPlaceholder"
            [(ngModel)]="searchTermValue"
            (click)="$event.stopPropagation()"
          />
        </div>
        }
        <div class="options-container">
          @if (showSelectAll && filteredOptions().length > 0) {
          <div class="option-item select-all" (click)="toggleSelectAll($event)">
            <input
              type="checkbox"
              class="option-checkbox"
              [checked]="isAllSelected()"
              [indeterminate]="hasPartialSelection()"
            />
            <span class="option-label select-all">Todos</span>
          </div>
          } @for (option of filteredOptions(); track option.value) {
          <div
            class="option-item"
            [class.disabled]="option.disabled"
            (click)="!option.disabled && toggleOption(option.value)"
          >
            <input
              type="checkbox"
              class="option-checkbox"
              [checked]="isSelected(option.value)"
              [disabled]="option.disabled"
            />
            <span class="option-label">{{ option.label }}</span>
          </div>
          } @if (filteredOptions().length === 0) {
          <div class="no-options">
            {{ noOptionsMessage }}
          </div>
          }
        </div>
      </div>
      }
    </div>
  `,
})
export class MultiSelectComponent<T = string>
  implements OnInit, ControlValueAccessor
{
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);

  @ViewChild('selectorElement') selectorElement!: ElementRef<HTMLDivElement>;

  @Input() options: FilterOption<T>[] = [];
  @Input() label = '';
  @Input() placeholder = 'Seleccionar';
  @Input() noOptionsMessage = 'No hay opciones disponibles';
  @Input() fullWidth = false;
  @Input() showSelectAll = true;
  @Input() maxSelectionDisplay = 1;
  @Input() multipleSelectionText = (count: number) => `${count} seleccionados`;
  @Input() allSelectedText = 'Todos';
  @Input() disabled = false;
  @Input() searchable = false;
  @Input() searchPlaceholder = 'Buscar...';
  @Input() size: SelectorSize = 'default';

  @Output() selectionChange = new EventEmitter<T[]>();

  control = new FormControl<T[]>([]);

  private searchTerm = signal('');
  searchTermValue = '';
  private onChange: (value: T[]) => void = () => {};
  private onTouched: () => void = () => {};

  selectedValues = signal<T[]>([]);

  isOpen = signal(false);
  isFocused = signal(false);
  dropdownPosition = signal<'top' | 'bottom'>('bottom');

  filteredOptions = computed(() => {
    const term = this.searchTerm().toLowerCase();
    if (!term) return this.options;
    return this.options.filter((option) =>
      option.label.toLowerCase().includes(term)
    );
  });

  selectedCount = computed(() => {
    return this.selectedValues().length;
  });

  hasSelection = computed(() => this.selectedCount() > 0);

  hasDisplayValue = computed(() => {
    return this.hasSelection();
  });

  isShowingTodos = computed(() => {
    const values = this.selectedValues();
    const availableOptions = this.options.filter((opt) => !opt.disabled);
    return (
      availableOptions.length > 0 &&
      availableOptions.every((opt) => values.includes(opt.value))
    );
  });

  triggerText = computed(() => {
    const count = this.selectedCount();
    const values = this.selectedValues();

    if (count === 0) {
      return this.placeholder;
    }

    const availableOptions = this.options.filter((opt) => !opt.disabled);
    const isAllSelected =
      availableOptions.length > 0 &&
      availableOptions.every((opt) => values.includes(opt.value));

    if (isAllSelected) {
      return 'Todos';
    }

    if (count === 1) {
      const option = this.options.find((opt) => opt.value === values[0]);
      return option?.label || this.placeholder;
    }

    return this.multipleSelectionText(count);
  });

  constructor() {
    effect(() => {
      this.searchTerm.set(this.searchTermValue);
    });

    effect(() => {
      if (this.isOpen()) {
        const handleClickOutside = (event: MouseEvent) => {
          if (!this.elementRef.nativeElement.contains(event.target as Node)) {
            this.isOpen.set(false);
          }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
      }
      return {};
    });
  }

  ngOnInit(): void {
    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values) => {
        const safeValues = values || [];
        this.onChange(safeValues);
        this.selectionChange.emit(safeValues);
      });
  }

  writeValue(values: T[] | null): void {
    const safeValues = values || [];
    this.selectedValues.set(safeValues);
    this.control.setValue(safeValues, { emitEvent: false });
  }

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }

  isSelected(value: T): boolean {
    return this.selectedValues().includes(value);
  }

  isAllSelected(): boolean {
    const values = this.selectedValues();
    const availableOptions = this.filteredOptions().filter(
      (opt) => !opt.disabled
    );
    return (
      availableOptions.length > 0 &&
      availableOptions.every((opt) => values.includes(opt.value))
    );
  }

  hasPartialSelection(): boolean {
    const values = this.selectedValues();
    const availableOptions = this.filteredOptions().filter(
      (opt) => !opt.disabled
    );
    return values.length > 0 && values.length < availableOptions.length;
  }

  private updateSelection(newValues: T[]): void {
    this.selectedValues.set(newValues);
    this.control.setValue(newValues);
  }

  toggleDropdown(): void {
    if (this.disabled) return;

    this.isOpen.update((v) => !v);
    this.isFocused.set(true);
    this.onTouched();

    if (this.isOpen()) {
      this.checkDropdownPosition();
    }
  }

  onBlur(): void {
    setTimeout(() => {
      this.isFocused.set(false);
    }, 200);
  }

  checkDropdownPosition(): void {
    if (!this.selectorElement) return;

    const rect = this.selectorElement.nativeElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < 250 && spaceAbove > spaceBelow) {
      this.dropdownPosition.set('top');
    } else {
      this.dropdownPosition.set('bottom');
    }
  }

  toggleOption(value: T): void {
    const currentValues = this.selectedValues();
    const index = currentValues.indexOf(value);
    if (index > -1) {
      const newValues = [...currentValues];
      newValues.splice(index, 1);
      this.updateSelection(newValues);
    } else {
      this.updateSelection([...currentValues, value]);
    }
  }

  toggleSelectAll(event: Event): void {
    event.stopPropagation();
    if (this.isAllSelected()) {
      this.updateSelection([]);
    } else {
      const availableValues = this.filteredOptions()
        .filter((opt) => !opt.disabled)
        .map((opt) => opt.value);
      this.updateSelection(availableValues);
    }
  }
}
